import { ReactNode } from 'react';

import CleanLinkButton from '../../../../../components/CleanLinkButton/CleanLinkButton';
import DimmableCard from '../../../../../components/DimmableCard/DimmableCard';
import PaddedBox from '../../../../../components/PaddedBox/PaddedBox';
import Paragraph from '../../../../../components/Paragraph/Paragraph';
import Row from '../../../../../components/Row/Row';
import Stack from '../../../../../components/Stack/Stack';
import useTrans from '../../../../../hooks/ui/useTrans';
import BookingParameters from '../../../../types/BookingParameters';
import compileStepUrl from '../../helpers/compileStepUrl';
import ComposeBookingStepsUrls from '../../types/ComposeBookingStepsUrls';

import styles from './BookingParametersBar.module.css';

type BarSegmentProps = {
    children: ReactNode;
    href?: string;
};

const BarSegment = ({ children, href }: BarSegmentProps) => {
    return (
        <CleanLinkButton
            href={href}
            fullWidth
            className={styles.BarSegment}
            visitOptions={{
                preserveScroll: true,
            }}
        >
            <PaddedBox verticalPadding="small" horizontal="center">
                <Stack space="none">{children}</Stack>
            </PaddedBox>
        </CleanLinkButton>
    );
};

type BookingParametersBarProps = {
    parameters: BookingParameters;
    stepUrls: ComposeBookingStepsUrls;
    dimmed?: boolean;
    noPassengers?: boolean;
};

const BookingParametersBar = ({
    parameters,
    stepUrls,
    dimmed = false,
    noPassengers = false,
}: BookingParametersBarProps) => {
    const trans = useTrans('CustomerArea.BookingForm.BookingParametersBar.');
    const { dock, numberOfPassengers, numberOfBoats } = parameters;
    const showNumberOfPassengers = !noPassengers && numberOfPassengers;

    if (!dock && !showNumberOfPassengers) {
        return null;
    }

    return (
        <DimmableCard
            isLast={false}
            verticalPadding="none"
            horizontalPadding="none"
            dimmed={dimmed}
            isFirst={false}
        >
            <Row
                horizontal="space-evenly"
                vertical="stretch"
                space="none"
                spacer={<span className={styles.Spacer} />}
            >
                {dock && (
                    <BarSegment
                        href={compileStepUrl('dock', stepUrls, parameters)}
                    >
                        <Paragraph bold align="center">
                            {dock.name}
                        </Paragraph>
                        <Paragraph subdued size="small" align="center">
                            {dock.address.line1} - {dock.address.city}
                        </Paragraph>
                    </BarSegment>
                )}

                {showNumberOfPassengers && (
                    <BarSegment
                        href={compileStepUrl(
                            'passengers',
                            stepUrls,
                            parameters
                        )}
                    >
                        <Paragraph bold align="center">
                            {trans('NumberOfPassengers', {
                                numberOfPassengers: numberOfPassengers,
                            })}
                        </Paragraph>
                        <Paragraph subdued size="small" align="center">
                            {trans('NumberOfBoats', {
                                numberOfBoats: numberOfBoats ?? 1,
                            })}
                        </Paragraph>
                    </BarSegment>
                )}
            </Row>
        </DimmableCard>
    );
};

export default BookingParametersBar;
